import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  limit,
  startAfter,
  DocumentSnapshot,
  writeBatch,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  Expense,
  ExpenseReport,
  ExpenseStatus,
  ExpenseCategory,
  ExpensePolicy,
  ExpenseBudget,
  ExpenseAnalytics,
  ExpenseFilter,
  ExpenseSortOptions,
  PaginatedExpenses,
  BulkOperationResult,
  PolicyViolation,
  ApprovalStep,
  PaymentMethod,
  ExpenseAttachment,
  ExpenseCategoryLabels,
  ExpenseStatusLabels,
} from '@/models/expense.model';

const EXPENSES_COLLECTION = 'expenses';
const EXPENSE_REPORTS_COLLECTION = 'expenseReports';
const EXPENSE_POLICIES_COLLECTION = 'expensePolicies';
const EXPENSE_BUDGETS_COLLECTION = 'expenseBudgets';

export class ExpenseService {
  // ============================================
  // EXPENSE CRUD OPERATIONS
  // ============================================

  /**
   * Get all expenses with optional filtering
   */
  static async getAllExpenses(): Promise<Expense[]> {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      orderBy('expenseDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToExpense(doc.id, doc.data()));
  }

  /**
   * Get expense by ID
   */
  static async getExpenseById(id: string): Promise<Expense | null> {
    const docRef = doc(db, EXPENSES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.docToExpense(docSnap.id, docSnap.data());
  }

  /**
   * Get expenses by employee
   */
  static async getExpensesByEmployee(employeeId: string): Promise<Expense[]> {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('expenseDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToExpense(doc.id, doc.data()));
  }

  /**
   * Get expenses by vendor
   */
  static async getExpensesByVendor(vendorId: string): Promise<Expense[]> {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('vendorId', '==', vendorId),
      orderBy('expenseDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToExpense(doc.id, doc.data()));
  }

  /**
   * Get expenses by status
   */
  static async getExpensesByStatus(status: ExpenseStatus): Promise<Expense[]> {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('status', '==', status),
      orderBy('expenseDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToExpense(doc.id, doc.data()));
  }

  /**
   * Get expenses by date range
   */
  static async getExpensesByDateRange(startDate: Date, endDate: Date): Promise<Expense[]> {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('expenseDate', '>=', Timestamp.fromDate(startDate)),
      where('expenseDate', '<=', Timestamp.fromDate(endDate)),
      orderBy('expenseDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToExpense(doc.id, doc.data()));
  }

  /**
   * Get expenses pending approval
   */
  static async getPendingApprovalExpenses(): Promise<Expense[]> {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('status', 'in', [ExpenseStatus.submitted, ExpenseStatus.pendingApproval]),
      orderBy('submittedAt', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToExpense(doc.id, doc.data()));
  }

  /**
   * Get filtered expenses with pagination
   */
  static async getFilteredExpenses(
    filter: ExpenseFilter,
    sort: ExpenseSortOptions = { field: 'expenseDate', direction: 'desc' },
    page: number = 1,
    pageSize: number = 25,
    lastDoc?: DocumentSnapshot
  ): Promise<PaginatedExpenses> {
    const constraints: QueryConstraint[] = [];
    
    // Apply filters
    if (filter.statuses && filter.statuses.length > 0) {
      constraints.push(where('status', 'in', filter.statuses));
    }
    
    if (filter.categories && filter.categories.length > 0) {
      constraints.push(where('category', 'in', filter.categories));
    }
    
    if (filter.employeeIds && filter.employeeIds.length > 0) {
      constraints.push(where('employeeId', 'in', filter.employeeIds));
    }
    
    if (filter.startDate) {
      constraints.push(where('expenseDate', '>=', Timestamp.fromDate(filter.startDate)));
    }
    
    if (filter.endDate) {
      constraints.push(where('expenseDate', '<=', Timestamp.fromDate(filter.endDate)));
    }
    
    if (filter.reportId) {
      constraints.push(where('reportId', '==', filter.reportId));
    }
    
    if (filter.billable !== undefined) {
      constraints.push(where('billable', '==', filter.billable));
    }
    
    if (filter.vendorIds && filter.vendorIds.length > 0) {
      constraints.push(where('vendorId', 'in', filter.vendorIds));
    }
    
    // Add sorting
    constraints.push(orderBy(sort.field, sort.direction));
    
    // Add pagination
    constraints.push(limit(pageSize));
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }
    
    const q = query(collection(db, EXPENSES_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);
    
    let expenses = snapshot.docs.map(doc => this.docToExpense(doc.id, doc.data()));
    
    // Apply client-side filters that can't be done in Firestore
    if (filter.minAmount !== undefined) {
      expenses = expenses.filter(e => e.amount >= filter.minAmount!);
    }
    if (filter.maxAmount !== undefined) {
      expenses = expenses.filter(e => e.amount <= filter.maxAmount!);
    }
    if (filter.searchQuery) {
      const search = filter.searchQuery.toLowerCase();
      expenses = expenses.filter(e => 
        e.description.toLowerCase().includes(search) ||
        e.merchant?.toLowerCase().includes(search) ||
        e.employeeName.toLowerCase().includes(search) ||
        e.notes?.toLowerCase().includes(search)
      );
    }
    if (filter.hasReceipt !== undefined) {
      expenses = expenses.filter(e => 
        filter.hasReceipt ? (e.attachments.length > 0 || e.receiptUrl) : (!e.attachments.length && !e.receiptUrl)
      );
    }
    if (filter.hasPolicyViolations !== undefined) {
      expenses = expenses.filter(e => 
        filter.hasPolicyViolations ? e.policyViolations.length > 0 : e.policyViolations.length === 0
      );
    }
    
    // Get total count (this is approximate for paginated queries)
    const countQuery = query(collection(db, EXPENSES_COLLECTION));
    const countSnapshot = await getDocs(countQuery);
    const total = countSnapshot.size;
    
    return {
      expenses,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
      hasMore: expenses.length === pageSize,
    };
  }

  /**
   * Create a new expense
   */
  static async createExpense(expenseData: Partial<Expense>): Promise<string> {
    console.log('[ExpenseService] createExpense called with:', {
      vendorId: expenseData.vendorId,
      vendorName: expenseData.vendorName,
      status: expenseData.status,
      employeeId: expenseData.employeeId,
      createdBy: expenseData.createdBy,
      description: expenseData.description,
      amount: expenseData.amount
    });
    
    const now = Timestamp.now();
    
    // Validate against policies
    const violations = await this.validateExpenseAgainstPolicies(expenseData);
    console.log('[ExpenseService] Policy violations:', violations);
    
    const data: Record<string, unknown> = {
      // Employee info
      employeeId: expenseData.employeeId || '',
      employeeName: expenseData.employeeName || '',
      employeeEmail: expenseData.employeeEmail || null,
      employeeDepartment: expenseData.employeeDepartment || null,
      
      // Report association
      reportId: expenseData.reportId || null,
      reportNumber: expenseData.reportNumber || null,
      
      // Core details
      category: expenseData.category || ExpenseCategory.other,
      subcategory: expenseData.subcategory || null,
      description: expenseData.description || '',
      amount: expenseData.amount || 0,
      currency: expenseData.currency || 'USD',
      exchangeRate: expenseData.exchangeRate || null,
      originalAmount: expenseData.originalAmount || null,
      originalCurrency: expenseData.originalCurrency || null,
      
      // Tax
      taxAmount: expenseData.taxAmount || null,
      taxRate: expenseData.taxRate || null,
      taxDeductible: expenseData.taxDeductible ?? true,
      
      // Dates
      expenseDate: expenseData.expenseDate ? Timestamp.fromDate(expenseData.expenseDate) : now,
      submittedAt: expenseData.submittedAt ? Timestamp.fromDate(expenseData.submittedAt) : null,
      
      // Payment
      paymentMethod: expenseData.paymentMethod || PaymentMethod.personalCard,
      cardLastFour: expenseData.cardLastFour || null,
      
      // Merchant
      merchant: expenseData.merchant || null,
      merchantCategory: expenseData.merchantCategory || null,
      vendorId: expenseData.vendorId || null,
      vendorName: expenseData.vendorName || null,
      location: expenseData.location || null,
      city: expenseData.city || null,
      state: expenseData.state || null,
      country: expenseData.country || null,
      
      // Attachments
      attachments: expenseData.attachments || [],
      receiptUrl: expenseData.receiptUrl || null,
      receiptFileName: expenseData.receiptFileName || null,
      receiptRequired: expenseData.receiptRequired ?? true,
      receiptVerified: expenseData.receiptVerified ?? false,
      
      // Mileage
      mileageDetails: expenseData.mileageDetails || null,
      
      // Project tracking
      projectId: expenseData.projectId || null,
      projectName: expenseData.projectName || null,
      projectCode: expenseData.projectCode || null,
      departmentId: expenseData.departmentId || null,
      departmentName: expenseData.departmentName || null,
      costCenterId: expenseData.costCenterId || null,
      costCenterName: expenseData.costCenterName || null,
      glAccountCode: expenseData.glAccountCode || null,
      
      // Billable
      billable: expenseData.billable ?? false,
      clientId: expenseData.clientId || null,
      clientName: expenseData.clientName || null,
      billedToClient: expenseData.billedToClient ?? false,
      billedDate: expenseData.billedDate ? Timestamp.fromDate(expenseData.billedDate) : null,
      invoiceNumber: expenseData.invoiceNumber || null,
      
      // Recurring
      isRecurring: expenseData.isRecurring ?? false,
      recurrence: expenseData.recurrence ? {
        ...expenseData.recurrence,
        startDate: Timestamp.fromDate(expenseData.recurrence.startDate),
        endDate: expenseData.recurrence.endDate ? Timestamp.fromDate(expenseData.recurrence.endDate) : null,
        nextOccurrence: expenseData.recurrence.nextOccurrence ? Timestamp.fromDate(expenseData.recurrence.nextOccurrence) : null,
      } : null,
      
      // Status
      status: expenseData.status || ExpenseStatus.draft,
      
      // Approval
      approvalWorkflow: expenseData.approvalWorkflow || [],
      currentApprovalStep: expenseData.currentApprovalStep || null,
      approvedBy: expenseData.approvedBy || null,
      approvedByName: expenseData.approvedByName || null,
      approvedAt: expenseData.approvedAt ? Timestamp.fromDate(expenseData.approvedAt) : null,
      rejectedBy: expenseData.rejectedBy || null,
      rejectedByName: expenseData.rejectedByName || null,
      rejectedAt: expenseData.rejectedAt ? Timestamp.fromDate(expenseData.rejectedAt) : null,
      rejectionReason: expenseData.rejectionReason || null,
      
      // Payment
      paidAt: expenseData.paidAt ? Timestamp.fromDate(expenseData.paidAt) : null,
      paidBy: expenseData.paidBy || null,
      paymentReference: expenseData.paymentReference || null,
      reimbursementMethod: expenseData.reimbursementMethod || null,
      
      // Policy
      policyViolations: violations,
      requiresAudit: expenseData.requiresAudit ?? false,
      auditedBy: expenseData.auditedBy || null,
      auditedAt: expenseData.auditedAt ? Timestamp.fromDate(expenseData.auditedAt) : null,
      auditNotes: expenseData.auditNotes || null,
      
      // Notes
      notes: expenseData.notes || null,
      internalNotes: expenseData.internalNotes || null,
      tags: expenseData.tags || [],
      customFields: expenseData.customFields || null,
      
      // Metadata
      createdAt: now,
      updatedAt: now,
      createdBy: expenseData.createdBy || null,
      lastModifiedBy: expenseData.lastModifiedBy || null,
    };
    
    console.log('[ExpenseService] Prepared Firestore data:', {
      vendorId: data.vendorId,
      vendorName: data.vendorName,
      status: data.status,
      employeeId: data.employeeId,
      createdBy: data.createdBy,
      description: data.description,
      amount: data.amount
    });
    
    try {
      const docRef = await addDoc(collection(db, EXPENSES_COLLECTION), data);
      console.log('[ExpenseService] Expense created successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('[ExpenseService] Error creating expense:', error);
      if (error instanceof Error) {
        console.error('[ExpenseService] Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        // Check if it's a permission error
        if (error.message.includes('permission') || error.message.includes('Permission')) {
          throw new Error(`Permission denied: You must be logged in as operations staff to create expenses. ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Update an expense
   */
  static async updateExpense(id: string, expenseData: Partial<Expense>): Promise<void> {
    const docRef = doc(db, EXPENSES_COLLECTION, id);
    const updateData: Record<string, unknown> = {
      ...expenseData,
      updatedAt: Timestamp.now(),
    };
    
    // Convert dates to Timestamps
    if (expenseData.expenseDate) {
      updateData.expenseDate = Timestamp.fromDate(expenseData.expenseDate);
    }
    if (expenseData.submittedAt) {
      updateData.submittedAt = Timestamp.fromDate(expenseData.submittedAt);
    }
    if (expenseData.approvedAt) {
      updateData.approvedAt = Timestamp.fromDate(expenseData.approvedAt);
    }
    if (expenseData.rejectedAt) {
      updateData.rejectedAt = Timestamp.fromDate(expenseData.rejectedAt);
    }
    if (expenseData.paidAt) {
      updateData.paidAt = Timestamp.fromDate(expenseData.paidAt);
    }
    
    await updateDoc(docRef, updateData);
  }

  /**
   * Delete an expense
   */
  static async deleteExpense(id: string): Promise<void> {
    const docRef = doc(db, EXPENSES_COLLECTION, id);
    await deleteDoc(docRef);
  }

  /**
   * Submit expense for approval
   */
  static async submitExpense(id: string): Promise<void> {
    const expense = await this.getExpenseById(id);
    if (!expense) throw new Error('Expense not found');
    
    await this.updateExpense(id, {
      status: ExpenseStatus.submitted,
      submittedAt: new Date(),
    });
  }

  /**
   * Approve an expense
   */
  static async approveExpense(
    id: string, 
    approverId: string, 
    approverName: string,
    comments?: string
  ): Promise<void> {
    const expense = await this.getExpenseById(id);
    if (!expense) throw new Error('Expense not found');
    
    const updatedWorkflow = [...expense.approvalWorkflow];
    if (expense.currentApprovalStep !== undefined && updatedWorkflow[expense.currentApprovalStep]) {
      updatedWorkflow[expense.currentApprovalStep] = {
        ...updatedWorkflow[expense.currentApprovalStep],
        status: 'approved',
        approverId,
        approverName,
        actionDate: new Date(),
        comments,
      };
    }
    
    // Check if all approval steps are complete
    const allApproved = updatedWorkflow.every(step => step.status === 'approved' || step.status === 'skipped');
    
    await this.updateExpense(id, {
      approvalWorkflow: updatedWorkflow,
      currentApprovalStep: allApproved ? undefined : (expense.currentApprovalStep || 0) + 1,
      status: allApproved ? ExpenseStatus.approved : ExpenseStatus.pendingApproval,
      approvedBy: allApproved ? approverId : expense.approvedBy,
      approvedByName: allApproved ? approverName : expense.approvedByName,
      approvedAt: allApproved ? new Date() : expense.approvedAt,
    });
  }

  /**
   * Reject an expense
   */
  static async rejectExpense(
    id: string,
    rejectorId: string,
    rejectorName: string,
    reason: string
  ): Promise<void> {
    const expense = await this.getExpenseById(id);
    if (!expense) throw new Error('Expense not found');
    
    const updatedWorkflow = [...expense.approvalWorkflow];
    if (expense.currentApprovalStep !== undefined && updatedWorkflow[expense.currentApprovalStep]) {
      updatedWorkflow[expense.currentApprovalStep] = {
        ...updatedWorkflow[expense.currentApprovalStep],
        status: 'rejected',
        approverId: rejectorId,
        approverName: rejectorName,
        actionDate: new Date(),
        comments: reason,
      };
    }
    
    await this.updateExpense(id, {
      approvalWorkflow: updatedWorkflow,
      status: ExpenseStatus.rejected,
      rejectedBy: rejectorId,
      rejectedByName: rejectorName,
      rejectedAt: new Date(),
      rejectionReason: reason,
    });
  }

  /**
   * Mark expense as paid
   */
  static async markExpenseAsPaid(
    id: string,
    paidBy: string,
    paymentReference?: string,
    reimbursementMethod?: string
  ): Promise<void> {
    await this.updateExpense(id, {
      status: ExpenseStatus.paid,
      paidAt: new Date(),
      paidBy,
      paymentReference,
      reimbursementMethod,
    });
  }

  /**
   * Bulk approve expenses
   */
  static async bulkApproveExpenses(
    ids: string[],
    approverId: string,
    approverName: string
  ): Promise<BulkOperationResult> {
    const result: BulkOperationResult = {
      successCount: 0,
      failureCount: 0,
      errors: [],
    };
    
    const batch = writeBatch(db);
    
    for (const id of ids) {
      try {
        const docRef = doc(db, EXPENSES_COLLECTION, id);
        batch.update(docRef, {
          status: ExpenseStatus.approved,
          approvedBy: approverId,
          approvedByName: approverName,
          approvedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        result.successCount++;
      } catch (error) {
        result.failureCount++;
        result.errors.push({ id, error: error instanceof Error ? error.message : 'Unknown error' });
      }
    }
    
    await batch.commit();
    return result;
  }

  /**
   * Bulk reject expenses
   */
  static async bulkRejectExpenses(
    ids: string[],
    rejectorId: string,
    rejectorName: string,
    reason: string
  ): Promise<BulkOperationResult> {
    const result: BulkOperationResult = {
      successCount: 0,
      failureCount: 0,
      errors: [],
    };
    
    const batch = writeBatch(db);
    
    for (const id of ids) {
      try {
        const docRef = doc(db, EXPENSES_COLLECTION, id);
        batch.update(docRef, {
          status: ExpenseStatus.rejected,
          rejectedBy: rejectorId,
          rejectedByName: rejectorName,
          rejectedAt: Timestamp.now(),
          rejectionReason: reason,
          updatedAt: Timestamp.now(),
        });
        result.successCount++;
      } catch (error) {
        result.failureCount++;
        result.errors.push({ id, error: error instanceof Error ? error.message : 'Unknown error' });
      }
    }
    
    await batch.commit();
    return result;
  }

  // ============================================
  // EXPENSE REPORT OPERATIONS
  // ============================================

  /**
   * Get all expense reports
   */
  static async getAllReports(): Promise<ExpenseReport[]> {
    const q = query(
      collection(db, EXPENSE_REPORTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToReport(doc.id, doc.data()));
  }

  /**
   * Get expense report by ID
   */
  static async getReportById(id: string): Promise<ExpenseReport | null> {
    const docRef = doc(db, EXPENSE_REPORTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    
    const report = this.docToReport(docSnap.id, docSnap.data());
    
    // Populate expenses if expenseIds exist
    if (report.expenseIds && report.expenseIds.length > 0) {
      const expenses = await Promise.all(
        report.expenseIds.map(id => this.getExpenseById(id))
      );
      report.expenses = expenses.filter((e): e is Expense => e !== null);
    }
    
    return report;
  }

  /**
   * Get reports by employee
   */
  static async getReportsByEmployee(employeeId: string): Promise<ExpenseReport[]> {
    const q = query(
      collection(db, EXPENSE_REPORTS_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToReport(doc.id, doc.data()));
  }

  /**
   * Get reports by status
   */
  static async getReportsByStatus(status: ExpenseStatus): Promise<ExpenseReport[]> {
    const q = query(
      collection(db, EXPENSE_REPORTS_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToReport(doc.id, doc.data()));
  }

  /**
   * Create expense report
   */
  static async createReport(reportData: Partial<ExpenseReport>): Promise<string> {
    const now = Timestamp.now();
    
    // Generate report number
    const reportNumber = `EXP-${Date.now().toString(36).toUpperCase()}`;
    
    // Calculate totals from expenses
    let subtotal = 0;
    let taxTotal = 0;
    let reimbursableAmount = 0;
    let nonReimbursableAmount = 0;
    let billableAmount = 0;
    const categoryBreakdown: Record<string, number> = {};
    
    if (reportData.expenseIds && reportData.expenseIds.length > 0) {
      const expenses = await Promise.all(
        reportData.expenseIds.map(id => this.getExpenseById(id))
      );
      
      for (const expense of expenses) {
        if (expense) {
          subtotal += expense.amount;
          taxTotal += expense.taxAmount || 0;
          
          // Track by category
          const category = expense.category;
          categoryBreakdown[category] = (categoryBreakdown[category] || 0) + expense.amount;
          
          // Track reimbursable vs non-reimbursable
          if (expense.paymentMethod === PaymentMethod.personalCard || expense.paymentMethod === PaymentMethod.cash) {
            reimbursableAmount += expense.amount;
          } else {
            nonReimbursableAmount += expense.amount;
          }
          
          // Track billable
          if (expense.billable) {
            billableAmount += expense.amount;
          }
        }
      }
    }
    
    const data: Record<string, unknown> = {
      reportNumber,
      title: reportData.title || `Expense Report - ${new Date().toLocaleDateString()}`,
      description: reportData.description || null,
      
      employeeId: reportData.employeeId || '',
      employeeName: reportData.employeeName || '',
      employeeEmail: reportData.employeeEmail || null,
      employeeDepartment: reportData.employeeDepartment || null,
      
      startDate: reportData.startDate ? Timestamp.fromDate(reportData.startDate) : now,
      endDate: reportData.endDate ? Timestamp.fromDate(reportData.endDate) : now,
      
      status: reportData.status || ExpenseStatus.draft,
      
      expenseIds: reportData.expenseIds || [],
      
      subtotal,
      taxTotal,
      total: subtotal + taxTotal,
      currency: reportData.currency || 'USD',
      
      categoryBreakdown,
      reimbursableAmount,
      nonReimbursableAmount,
      billableAmount,
      
      approvalWorkflow: reportData.approvalWorkflow || [],
      currentApprovalStep: reportData.currentApprovalStep || null,
      
      submittedAt: reportData.submittedAt ? Timestamp.fromDate(reportData.submittedAt) : null,
      approvedBy: reportData.approvedBy || null,
      approvedByName: reportData.approvedByName || null,
      approvedAt: reportData.approvedAt ? Timestamp.fromDate(reportData.approvedAt) : null,
      rejectedBy: reportData.rejectedBy || null,
      rejectedAt: reportData.rejectedAt ? Timestamp.fromDate(reportData.rejectedAt) : null,
      rejectionReason: reportData.rejectionReason || null,
      
      paidAt: reportData.paidAt ? Timestamp.fromDate(reportData.paidAt) : null,
      paidBy: reportData.paidBy || null,
      paymentReference: reportData.paymentReference || null,
      paymentMethod: reportData.paymentMethod || null,
      
      notes: reportData.notes || null,
      internalNotes: reportData.internalNotes || null,
      tags: reportData.tags || [],
      
      createdAt: now,
      updatedAt: now,
      createdBy: reportData.createdBy || null,
    };
    
    const docRef = await addDoc(collection(db, EXPENSE_REPORTS_COLLECTION), data);
    
    // Update expenses to reference this report
    if (reportData.expenseIds && reportData.expenseIds.length > 0) {
      const batch = writeBatch(db);
      for (const expenseId of reportData.expenseIds) {
        const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
        batch.update(expenseRef, { 
          reportId: docRef.id, 
          reportNumber,
          updatedAt: now 
        });
      }
      await batch.commit();
    }
    
    return docRef.id;
  }

  /**
   * Update expense report
   */
  static async updateReport(id: string, reportData: Partial<ExpenseReport>): Promise<void> {
    const docRef = doc(db, EXPENSE_REPORTS_COLLECTION, id);
    const updateData: Record<string, unknown> = {
      ...reportData,
      updatedAt: Timestamp.now(),
    };
    
    // Convert dates
    if (reportData.startDate) updateData.startDate = Timestamp.fromDate(reportData.startDate);
    if (reportData.endDate) updateData.endDate = Timestamp.fromDate(reportData.endDate);
    if (reportData.submittedAt) updateData.submittedAt = Timestamp.fromDate(reportData.submittedAt);
    if (reportData.approvedAt) updateData.approvedAt = Timestamp.fromDate(reportData.approvedAt);
    if (reportData.paidAt) updateData.paidAt = Timestamp.fromDate(reportData.paidAt);
    
    await updateDoc(docRef, updateData);
  }

  /**
   * Delete expense report
   */
  static async deleteReport(id: string): Promise<void> {
    // First, remove report reference from expenses
    const report = await this.getReportById(id);
    if (report && report.expenseIds) {
      const batch = writeBatch(db);
      for (const expenseId of report.expenseIds) {
        const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
        batch.update(expenseRef, { reportId: null, reportNumber: null });
      }
      await batch.commit();
    }
    
    const docRef = doc(db, EXPENSE_REPORTS_COLLECTION, id);
    await deleteDoc(docRef);
  }

  /**
   * Submit report for approval
   */
  static async submitReport(id: string): Promise<void> {
    await this.updateReport(id, {
      status: ExpenseStatus.submitted,
      submittedAt: new Date(),
    });
  }

  /**
   * Add expenses to report
   */
  static async addExpensesToReport(reportId: string, expenseIds: string[]): Promise<void> {
    const report = await this.getReportById(reportId);
    if (!report) throw new Error('Report not found');
    
    const newExpenseIds = [...new Set([...report.expenseIds, ...expenseIds])];
    
    // Recalculate totals
    let subtotal = 0;
    let taxTotal = 0;
    const categoryBreakdown: Record<string, number> = {};
    
    const expenses = await Promise.all(newExpenseIds.map(id => this.getExpenseById(id)));
    for (const expense of expenses) {
      if (expense) {
        subtotal += expense.amount;
        taxTotal += expense.taxAmount || 0;
        categoryBreakdown[expense.category] = (categoryBreakdown[expense.category] || 0) + expense.amount;
      }
    }
    
    await this.updateReport(reportId, {
      expenseIds: newExpenseIds,
      subtotal,
      taxTotal,
      total: subtotal + taxTotal,
      categoryBreakdown,
    });
    
    // Update expenses to reference this report
    const batch = writeBatch(db);
    for (const expenseId of expenseIds) {
      const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
      batch.update(expenseRef, { reportId, reportNumber: report.reportNumber });
    }
    await batch.commit();
  }

  /**
   * Remove expense from report
   */
  static async removeExpenseFromReport(reportId: string, expenseId: string): Promise<void> {
    const report = await this.getReportById(reportId);
    if (!report) throw new Error('Report not found');
    
    const newExpenseIds = report.expenseIds.filter(id => id !== expenseId);
    
    // Recalculate totals
    let subtotal = 0;
    let taxTotal = 0;
    const categoryBreakdown: Record<string, number> = {};
    
    const expenses = await Promise.all(newExpenseIds.map(id => this.getExpenseById(id)));
    for (const expense of expenses) {
      if (expense) {
        subtotal += expense.amount;
        taxTotal += expense.taxAmount || 0;
        categoryBreakdown[expense.category] = (categoryBreakdown[expense.category] || 0) + expense.amount;
      }
    }
    
    await this.updateReport(reportId, {
      expenseIds: newExpenseIds,
      subtotal,
      taxTotal,
      total: subtotal + taxTotal,
      categoryBreakdown,
    });
    
    // Remove report reference from expense
    const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
    await updateDoc(expenseRef, { reportId: null, reportNumber: null });
  }

  // ============================================
  // EXPENSE POLICY OPERATIONS
  // ============================================

  /**
   * Get all expense policies
   */
  static async getAllPolicies(): Promise<ExpensePolicy[]> {
    const q = query(
      collection(db, EXPENSE_POLICIES_COLLECTION),
      where('isActive', '==', true),
      orderBy('priority', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPolicy(doc.id, doc.data()));
  }

  /**
   * Get policy by ID
   */
  static async getPolicyById(id: string): Promise<ExpensePolicy | null> {
    const docRef = doc(db, EXPENSE_POLICIES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.docToPolicy(docSnap.id, docSnap.data());
  }

  /**
   * Create expense policy
   */
  static async createPolicy(policyData: Partial<ExpensePolicy>): Promise<string> {
    const now = Timestamp.now();
    const data: Record<string, unknown> = {
      ...policyData,
      effectiveDate: policyData.effectiveDate ? Timestamp.fromDate(policyData.effectiveDate) : now,
      expirationDate: policyData.expirationDate ? Timestamp.fromDate(policyData.expirationDate) : null,
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(db, EXPENSE_POLICIES_COLLECTION), data);
    return docRef.id;
  }

  /**
   * Update expense policy
   */
  static async updatePolicy(id: string, policyData: Partial<ExpensePolicy>): Promise<void> {
    const docRef = doc(db, EXPENSE_POLICIES_COLLECTION, id);
    await updateDoc(docRef, {
      ...policyData,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Validate expense against policies
   */
  static async validateExpenseAgainstPolicies(expense: Partial<Expense>): Promise<PolicyViolation[]> {
    const violations: PolicyViolation[] = [];
    const policies = await this.getAllPolicies();
    
    for (const policy of policies) {
      // Check category limits
      if (expense.category && expense.amount) {
        const categoryLimit = policy.categoryLimits?.find(cl => cl.category === expense.category);
        if (categoryLimit) {
          if (categoryLimit.singleExpenseLimit && expense.amount > categoryLimit.singleExpenseLimit) {
            violations.push({
              ruleId: `${policy.id}-category-limit`,
              ruleName: policy.name,
              violationType: 'error',
              message: `Expense exceeds ${ExpenseCategoryLabels[expense.category]} single expense limit of $${categoryLimit.singleExpenseLimit}`,
              suggestedAction: 'Split expense or request approval from manager',
            });
          }
          
          if (categoryLimit.requiresReceipt && !expense.receiptUrl && (!expense.attachments || expense.attachments.length === 0)) {
            violations.push({
              ruleId: `${policy.id}-receipt-required`,
              ruleName: policy.name,
              violationType: 'warning',
              message: `Receipt required for ${ExpenseCategoryLabels[expense.category]} expenses`,
              suggestedAction: 'Please attach a receipt',
            });
          }
        }
      }
      
      // Check general limits
      if (expense.amount && policy.generalLimits?.singleExpenseLimit) {
        if (expense.amount > policy.generalLimits.singleExpenseLimit) {
          violations.push({
            ruleId: `${policy.id}-general-limit`,
            ruleName: policy.name,
            violationType: 'error',
            message: `Expense exceeds general single expense limit of $${policy.generalLimits.singleExpenseLimit}`,
            suggestedAction: 'Split expense or request special approval',
          });
        }
      }
      
      // Check receipt requirements
      if (expense.amount && policy.receiptRequirements?.minimumAmount) {
        if (expense.amount >= policy.receiptRequirements.minimumAmount) {
          if (!expense.receiptUrl && (!expense.attachments || expense.attachments.length === 0)) {
            violations.push({
              ruleId: `${policy.id}-receipt-min`,
              ruleName: policy.name,
              violationType: 'warning',
              message: `Receipt required for expenses $${policy.receiptRequirements.minimumAmount} and above`,
              suggestedAction: 'Please attach a receipt',
            });
          }
        }
      }
      
      // Check blocked categories
      if (expense.category && policy.restrictions?.blockedCategories?.includes(expense.category)) {
        violations.push({
          ruleId: `${policy.id}-blocked-category`,
          ruleName: policy.name,
          violationType: 'error',
          message: `${ExpenseCategoryLabels[expense.category]} expenses are not allowed`,
          suggestedAction: 'Contact finance department for alternatives',
        });
      }
    }
    
    return violations;
  }

  // ============================================
  // EXPENSE BUDGET OPERATIONS
  // ============================================

  /**
   * Get all budgets
   */
  static async getAllBudgets(): Promise<ExpenseBudget[]> {
    const q = query(
      collection(db, EXPENSE_BUDGETS_COLLECTION),
      where('isActive', '==', true),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToBudget(doc.id, doc.data()));
  }

  /**
   * Get budget by ID
   */
  static async getBudgetById(id: string): Promise<ExpenseBudget | null> {
    const docRef = doc(db, EXPENSE_BUDGETS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.docToBudget(docSnap.id, docSnap.data());
  }

  /**
   * Get budget for department
   */
  static async getBudgetForDepartment(departmentId: string): Promise<ExpenseBudget | null> {
    const q = query(
      collection(db, EXPENSE_BUDGETS_COLLECTION),
      where('scope.departmentId', '==', departmentId),
      where('isActive', '==', true),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return this.docToBudget(snapshot.docs[0].id, snapshot.docs[0].data());
  }

  /**
   * Create budget
   */
  static async createBudget(budgetData: Partial<ExpenseBudget>): Promise<string> {
    const now = Timestamp.now();
    const data: Record<string, unknown> = {
      ...budgetData,
      startDate: budgetData.startDate ? Timestamp.fromDate(budgetData.startDate) : now,
      endDate: budgetData.endDate ? Timestamp.fromDate(budgetData.endDate) : now,
      totalSpent: 0,
      totalCommitted: 0,
      totalRemaining: budgetData.totalBudget || 0,
      percentUsed: 0,
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(db, EXPENSE_BUDGETS_COLLECTION), data);
    return docRef.id;
  }

  /**
   * Update budget spending
   */
  static async updateBudgetSpending(budgetId: string): Promise<void> {
    const budget = await this.getBudgetById(budgetId);
    if (!budget) return;
    
    // Calculate actual spending
    const expenses = await this.getExpensesByDateRange(budget.startDate, budget.endDate);
    
    let totalSpent = 0;
    let totalCommitted = 0;
    const categorySpending: Record<string, number> = {};
    
    for (const expense of expenses) {
      // Filter by scope if applicable
      if (budget.scope.departmentId && expense.departmentId !== budget.scope.departmentId) continue;
      if (budget.scope.projectId && expense.projectId !== budget.scope.projectId) continue;
      
      if (expense.status === ExpenseStatus.paid) {
        totalSpent += expense.amount;
      } else if (expense.status === ExpenseStatus.approved || expense.status === ExpenseStatus.pendingPayment) {
        totalCommitted += expense.amount;
      }
      
      categorySpending[expense.category] = (categorySpending[expense.category] || 0) + expense.amount;
    }
    
    const totalRemaining = budget.totalBudget - totalSpent - totalCommitted;
    const percentUsed = budget.totalBudget > 0 ? ((totalSpent + totalCommitted) / budget.totalBudget) * 100 : 0;
    
    // Update category budgets
    const categoryBudgets = budget.categoryBudgets.map(cb => ({
      ...cb,
      spent: categorySpending[cb.category] || 0,
      remaining: cb.budgetAmount - (categorySpending[cb.category] || 0),
      percentUsed: cb.budgetAmount > 0 ? ((categorySpending[cb.category] || 0) / cb.budgetAmount) * 100 : 0,
    }));
    
    await updateDoc(doc(db, EXPENSE_BUDGETS_COLLECTION, budgetId), {
      totalSpent,
      totalCommitted,
      totalRemaining,
      percentUsed,
      categoryBudgets,
      updatedAt: Timestamp.now(),
    });
  }

  // ============================================
  // ANALYTICS
  // ============================================

  /**
   * Get expense analytics for a period
   */
  static async getExpenseAnalytics(
    startDate: Date,
    endDate: Date,
    periodType: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month'
  ): Promise<ExpenseAnalytics> {
    const expenses = await this.getExpensesByDateRange(startDate, endDate);
    
    // Calculate summary metrics
    const totalExpenses = expenses.length;
    const totalAmount = expenses.reduce((sum, e) => sum + e.amount, 0);
    const averageExpenseAmount = totalExpenses > 0 ? totalAmount / totalExpenses : 0;
    
    // Calculate median
    const sortedAmounts = expenses.map(e => e.amount).sort((a, b) => a - b);
    const medianExpenseAmount = totalExpenses > 0 
      ? sortedAmounts[Math.floor(totalExpenses / 2)] 
      : 0;
    
    // By status
    const byStatus: Record<ExpenseStatus, { count: number; amount: number }> = {} as Record<ExpenseStatus, { count: number; amount: number }>;
    for (const status of Object.values(ExpenseStatus)) {
      const statusExpenses = expenses.filter(e => e.status === status);
      byStatus[status] = {
        count: statusExpenses.length,
        amount: statusExpenses.reduce((sum, e) => sum + e.amount, 0),
      };
    }
    
    // By category
    const categoryMap = new Map<ExpenseCategory, { count: number; amount: number }>();
    for (const expense of expenses) {
      const current = categoryMap.get(expense.category) || { count: 0, amount: 0 };
      categoryMap.set(expense.category, {
        count: current.count + 1,
        amount: current.amount + expense.amount,
      });
    }
    const byCategory = Array.from(categoryMap.entries()).map(([category, data]) => ({
      category,
      count: data.count,
      amount: data.amount,
      percentOfTotal: totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0,
      avgAmount: data.count > 0 ? data.amount / data.count : 0,
    })).sort((a, b) => b.amount - a.amount);
    
    // By department
    const departmentMap = new Map<string, { name: string; count: number; amount: number }>();
    for (const expense of expenses) {
      if (expense.departmentId) {
        const current = departmentMap.get(expense.departmentId) || { 
          name: expense.departmentName || 'Unknown', 
          count: 0, 
          amount: 0 
        };
        departmentMap.set(expense.departmentId, {
          name: current.name,
          count: current.count + 1,
          amount: current.amount + expense.amount,
        });
      }
    }
    const byDepartment = Array.from(departmentMap.entries()).map(([departmentId, data]) => ({
      departmentId,
      departmentName: data.name,
      count: data.count,
      amount: data.amount,
    })).sort((a, b) => b.amount - a.amount);
    
    // By employee (top spenders)
    const employeeMap = new Map<string, { name: string; count: number; amount: number }>();
    for (const expense of expenses) {
      const current = employeeMap.get(expense.employeeId) || { 
        name: expense.employeeName, 
        count: 0, 
        amount: 0 
      };
      employeeMap.set(expense.employeeId, {
        name: current.name,
        count: current.count + 1,
        amount: current.amount + expense.amount,
      });
    }
    const byEmployee = Array.from(employeeMap.entries()).map(([employeeId, data]) => ({
      employeeId,
      employeeName: data.name,
      count: data.count,
      amount: data.amount,
      avgAmount: data.count > 0 ? data.amount / data.count : 0,
    })).sort((a, b) => b.amount - a.amount).slice(0, 10);
    
    // By project
    const projectMap = new Map<string, { name: string; count: number; amount: number; billable: number }>();
    for (const expense of expenses) {
      if (expense.projectId) {
        const current = projectMap.get(expense.projectId) || { 
          name: expense.projectName || 'Unknown', 
          count: 0, 
          amount: 0,
          billable: 0,
        };
        projectMap.set(expense.projectId, {
          name: current.name,
          count: current.count + 1,
          amount: current.amount + expense.amount,
          billable: current.billable + (expense.billable ? expense.amount : 0),
        });
      }
    }
    const byProject = Array.from(projectMap.entries()).map(([projectId, data]) => ({
      projectId,
      projectName: data.name,
      count: data.count,
      amount: data.amount,
      billableAmount: data.billable,
    })).sort((a, b) => b.amount - a.amount);
    
    // Trend data
    const trendMap = new Map<string, { count: number; amount: number }>();
    for (const expense of expenses) {
      const dateKey = expense.expenseDate.toISOString().split('T')[0];
      const current = trendMap.get(dateKey) || { count: 0, amount: 0 };
      trendMap.set(dateKey, {
        count: current.count + 1,
        amount: current.amount + expense.amount,
      });
    }
    const trend = Array.from(trendMap.entries()).map(([dateStr, data]) => ({
      date: new Date(dateStr),
      count: data.count,
      amount: data.amount,
    })).sort((a, b) => a.date.getTime() - b.date.getTime());
    
    // Approval metrics
    const submittedExpenses = expenses.filter(e => e.submittedAt && (e.approvedAt || e.rejectedAt));
    let totalApprovalTime = 0;
    for (const expense of submittedExpenses) {
      if (expense.submittedAt && expense.approvedAt) {
        totalApprovalTime += expense.approvedAt.getTime() - expense.submittedAt.getTime();
      }
    }
    const avgApprovalTime = submittedExpenses.length > 0 
      ? totalApprovalTime / submittedExpenses.length / (1000 * 60 * 60) // Convert to hours
      : 0;
    
    const pendingExpenses = expenses.filter(e => 
      e.status === ExpenseStatus.submitted || e.status === ExpenseStatus.pendingApproval
    );
    const rejectedExpenses = expenses.filter(e => e.status === ExpenseStatus.rejected);
    
    // Policy compliance
    let totalViolations = 0;
    const violationsByType: Record<string, number> = {};
    for (const expense of expenses) {
      totalViolations += expense.policyViolations.length;
      for (const violation of expense.policyViolations) {
        violationsByType[violation.violationType] = (violationsByType[violation.violationType] || 0) + 1;
      }
    }
    const expensesWithViolations = expenses.filter(e => e.policyViolations.length > 0).length;
    const complianceRate = totalExpenses > 0 
      ? ((totalExpenses - expensesWithViolations) / totalExpenses) * 100 
      : 100;
    
    return {
      period: {
        startDate,
        endDate,
        periodType,
      },
      totalExpenses,
      totalAmount,
      averageExpenseAmount,
      medianExpenseAmount,
      byStatus,
      byCategory,
      byDepartment,
      byEmployee,
      byProject,
      trend,
      approvalMetrics: {
        avgApprovalTime,
        pendingCount: pendingExpenses.length,
        pendingAmount: pendingExpenses.reduce((sum, e) => sum + e.amount, 0),
        rejectionRate: totalExpenses > 0 ? (rejectedExpenses.length / totalExpenses) * 100 : 0,
      },
      policyCompliance: {
        totalViolations,
        violationsByType,
        complianceRate,
      },
    };
  }

  /**
   * Get spending by category for a period
   */
  static async getSpendingByCategory(
    startDate: Date,
    endDate: Date
  ): Promise<{ category: ExpenseCategory; amount: number; count: number }[]> {
    const expenses = await this.getExpensesByDateRange(startDate, endDate);
    
    const categoryMap = new Map<ExpenseCategory, { amount: number; count: number }>();
    for (const expense of expenses) {
      if (expense.status === ExpenseStatus.paid || expense.status === ExpenseStatus.approved) {
        const current = categoryMap.get(expense.category) || { amount: 0, count: 0 };
        categoryMap.set(expense.category, {
          amount: current.amount + expense.amount,
          count: current.count + 1,
        });
      }
    }
    
    return Array.from(categoryMap.entries())
      .map(([category, data]) => ({ category, ...data }))
      .sort((a, b) => b.amount - a.amount);
  }

  /**
   * Get monthly spending trend
   */
  static async getMonthlySpendingTrend(
    months: number = 12
  ): Promise<{ month: string; amount: number; count: number }[]> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);
    
    const expenses = await this.getExpensesByDateRange(startDate, endDate);
    
    const monthlyMap = new Map<string, { amount: number; count: number }>();
    for (const expense of expenses) {
      if (expense.status === ExpenseStatus.paid || expense.status === ExpenseStatus.approved) {
        const monthKey = `${expense.expenseDate.getFullYear()}-${String(expense.expenseDate.getMonth() + 1).padStart(2, '0')}`;
        const current = monthlyMap.get(monthKey) || { amount: 0, count: 0 };
        monthlyMap.set(monthKey, {
          amount: current.amount + expense.amount,
          count: current.count + 1,
        });
      }
    }
    
    return Array.from(monthlyMap.entries())
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }

  // ============================================
  // EXPORT FUNCTIONALITY
  // ============================================

  /**
   * Export expenses to CSV format
   */
  static exportExpensesToCSV(expenses: Expense[]): string {
    const headers = [
      'ID',
      'Date',
      'Employee',
      'Department',
      'Category',
      'Description',
      'Amount',
      'Currency',
      'Status',
      'Merchant',
      'Payment Method',
      'Project',
      'Billable',
      'Has Receipt',
      'Submitted At',
      'Approved By',
      'Approved At',
      'Notes',
    ];
    
    const rows = expenses.map(expense => [
      expense.id,
      expense.expenseDate.toISOString().split('T')[0],
      expense.employeeName,
      expense.departmentName || '',
      ExpenseCategoryLabels[expense.category] || expense.category,
      `"${expense.description.replace(/"/g, '""')}"`,
      expense.amount.toFixed(2),
      expense.currency,
      ExpenseStatusLabels[expense.status] || expense.status,
      expense.merchant || '',
      expense.paymentMethod || '',
      expense.projectName || '',
      expense.billable ? 'Yes' : 'No',
      (expense.attachments.length > 0 || expense.receiptUrl) ? 'Yes' : 'No',
      expense.submittedAt?.toISOString().split('T')[0] || '',
      expense.approvedByName || '',
      expense.approvedAt?.toISOString().split('T')[0] || '',
      `"${(expense.notes || '').replace(/"/g, '""')}"`,
    ]);
    
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  }

  // ============================================
  // DATA CONVERSION HELPERS
  // ============================================

  private static docToExpense(id: string, data: Record<string, unknown>): Expense {
    return {
      id,
      employeeId: (data.employeeId as string) || '',
      employeeName: (data.employeeName as string) || '',
      employeeEmail: data.employeeEmail as string | undefined,
      employeeDepartment: data.employeeDepartment as string | undefined,
      reportId: data.reportId as string | undefined,
      reportNumber: data.reportNumber as string | undefined,
      category: (data.category as ExpenseCategory) || ExpenseCategory.other,
      subcategory: data.subcategory as string | undefined,
      description: (data.description as string) || '',
      amount: (data.amount as number) || 0,
      currency: (data.currency as string) || 'USD',
      exchangeRate: data.exchangeRate as number | undefined,
      originalAmount: data.originalAmount as number | undefined,
      originalCurrency: data.originalCurrency as string | undefined,
      taxAmount: data.taxAmount as number | undefined,
      taxRate: data.taxRate as number | undefined,
      taxDeductible: (data.taxDeductible as boolean) ?? true,
      expenseDate: (data.expenseDate as Timestamp)?.toDate() || new Date(),
      submittedAt: (data.submittedAt as Timestamp)?.toDate(),
      paymentMethod: (data.paymentMethod as PaymentMethod) || PaymentMethod.personalCard,
      cardLastFour: data.cardLastFour as string | undefined,
      merchant: data.merchant as string | undefined,
      merchantCategory: data.merchantCategory as string | undefined,
      vendorId: data.vendorId as string | undefined,
      vendorName: data.vendorName as string | undefined,
      location: data.location as string | undefined,
      city: data.city as string | undefined,
      state: data.state as string | undefined,
      country: data.country as string | undefined,
      attachments: (data.attachments as ExpenseAttachment[]) || [],
      receiptUrl: data.receiptUrl as string | undefined,
      receiptFileName: data.receiptFileName as string | undefined,
      receiptRequired: (data.receiptRequired as boolean) ?? true,
      receiptVerified: (data.receiptVerified as boolean) ?? false,
      mileageDetails: data.mileageDetails as Expense['mileageDetails'],
      projectId: data.projectId as string | undefined,
      projectName: data.projectName as string | undefined,
      projectCode: data.projectCode as string | undefined,
      departmentId: data.departmentId as string | undefined,
      departmentName: data.departmentName as string | undefined,
      costCenterId: data.costCenterId as string | undefined,
      costCenterName: data.costCenterName as string | undefined,
      glAccountCode: data.glAccountCode as string | undefined,
      billable: (data.billable as boolean) ?? false,
      clientId: data.clientId as string | undefined,
      clientName: data.clientName as string | undefined,
      billedToClient: (data.billedToClient as boolean) ?? false,
      billedDate: (data.billedDate as Timestamp)?.toDate(),
      invoiceNumber: data.invoiceNumber as string | undefined,
      isRecurring: (data.isRecurring as boolean) ?? false,
      recurrence: data.recurrence ? {
        frequency: (data.recurrence as Record<string, unknown>).frequency as Expense['recurrence'] extends undefined ? never : NonNullable<Expense['recurrence']>['frequency'],
        startDate: ((data.recurrence as Record<string, unknown>).startDate as Timestamp)?.toDate() || new Date(),
        endDate: ((data.recurrence as Record<string, unknown>).endDate as Timestamp)?.toDate(),
        nextOccurrence: ((data.recurrence as Record<string, unknown>).nextOccurrence as Timestamp)?.toDate(),
        parentExpenseId: (data.recurrence as Record<string, unknown>).parentExpenseId as string | undefined,
      } : undefined,
      status: (data.status as ExpenseStatus) || ExpenseStatus.draft,
      approvalWorkflow: (data.approvalWorkflow as ApprovalStep[]) || [],
      currentApprovalStep: data.currentApprovalStep as number | undefined,
      approvedBy: data.approvedBy as string | undefined,
      approvedByName: data.approvedByName as string | undefined,
      approvedAt: (data.approvedAt as Timestamp)?.toDate(),
      rejectedBy: data.rejectedBy as string | undefined,
      rejectedByName: data.rejectedByName as string | undefined,
      rejectedAt: (data.rejectedAt as Timestamp)?.toDate(),
      rejectionReason: data.rejectionReason as string | undefined,
      paidAt: (data.paidAt as Timestamp)?.toDate(),
      paidBy: data.paidBy as string | undefined,
      paymentReference: data.paymentReference as string | undefined,
      reimbursementMethod: data.reimbursementMethod as string | undefined,
      policyViolations: (data.policyViolations as PolicyViolation[]) || [],
      requiresAudit: (data.requiresAudit as boolean) ?? false,
      auditedBy: data.auditedBy as string | undefined,
      auditedAt: (data.auditedAt as Timestamp)?.toDate(),
      auditNotes: data.auditNotes as string | undefined,
      notes: data.notes as string | undefined,
      internalNotes: data.internalNotes as string | undefined,
      tags: (data.tags as string[]) || [],
      customFields: data.customFields as Record<string, unknown> | undefined,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      createdBy: data.createdBy as string | undefined,
      lastModifiedBy: data.lastModifiedBy as string | undefined,
    };
  }

  private static docToReport(id: string, data: Record<string, unknown>): ExpenseReport {
    return {
      id,
      reportNumber: (data.reportNumber as string) || '',
      title: (data.title as string) || '',
      description: data.description as string | undefined,
      employeeId: (data.employeeId as string) || '',
      employeeName: (data.employeeName as string) || '',
      employeeEmail: data.employeeEmail as string | undefined,
      employeeDepartment: data.employeeDepartment as string | undefined,
      startDate: (data.startDate as Timestamp)?.toDate() || new Date(),
      endDate: (data.endDate as Timestamp)?.toDate() || new Date(),
      status: (data.status as ExpenseStatus) || ExpenseStatus.draft,
      expenseIds: (data.expenseIds as string[]) || [],
      expenses: undefined,
      subtotal: (data.subtotal as number) || 0,
      taxTotal: (data.taxTotal as number) || 0,
      total: (data.total as number) || 0,
      currency: (data.currency as string) || 'USD',
      categoryBreakdown: (data.categoryBreakdown as Record<string, number>) || {},
      reimbursableAmount: (data.reimbursableAmount as number) || 0,
      nonReimbursableAmount: (data.nonReimbursableAmount as number) || 0,
      billableAmount: (data.billableAmount as number) || 0,
      approvalWorkflow: (data.approvalWorkflow as ApprovalStep[]) || [],
      currentApprovalStep: data.currentApprovalStep as number | undefined,
      submittedAt: (data.submittedAt as Timestamp)?.toDate(),
      approvedBy: data.approvedBy as string | undefined,
      approvedByName: data.approvedByName as string | undefined,
      approvedAt: (data.approvedAt as Timestamp)?.toDate(),
      rejectedBy: data.rejectedBy as string | undefined,
      rejectedAt: (data.rejectedAt as Timestamp)?.toDate(),
      rejectionReason: data.rejectionReason as string | undefined,
      paidAt: (data.paidAt as Timestamp)?.toDate(),
      paidBy: data.paidBy as string | undefined,
      paymentReference: data.paymentReference as string | undefined,
      paymentMethod: data.paymentMethod as string | undefined,
      notes: data.notes as string | undefined,
      internalNotes: data.internalNotes as string | undefined,
      tags: (data.tags as string[]) || [],
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      createdBy: data.createdBy as string | undefined,
    };
  }

  private static docToPolicy(id: string, data: Record<string, unknown>): ExpensePolicy {
    return {
      id,
      name: (data.name as string) || '',
      description: (data.description as string) || '',
      isActive: (data.isActive as boolean) ?? true,
      priority: (data.priority as number) || 0,
      scope: (data.scope as ExpensePolicy['scope']) || { allEmployees: true },
      categoryLimits: (data.categoryLimits as ExpensePolicy['categoryLimits']) || [],
      generalLimits: (data.generalLimits as ExpensePolicy['generalLimits']) || {},
      approvalThresholds: (data.approvalThresholds as ExpensePolicy['approvalThresholds']) || [],
      receiptRequirements: (data.receiptRequirements as ExpensePolicy['receiptRequirements']) || {
        minimumAmount: 25,
        requiredCategories: [],
        exemptCategories: [],
      },
      restrictions: (data.restrictions as ExpensePolicy['restrictions']) || {},
      mileageRate: (data.mileageRate as number) || 0.67, // IRS 2024 rate
      perDiemRates: data.perDiemRates as ExpensePolicy['perDiemRates'],
      effectiveDate: (data.effectiveDate as Timestamp)?.toDate() || new Date(),
      expirationDate: (data.expirationDate as Timestamp)?.toDate(),
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      createdBy: data.createdBy as string | undefined,
      lastModifiedBy: data.lastModifiedBy as string | undefined,
    };
  }

  private static docToBudget(id: string, data: Record<string, unknown>): ExpenseBudget {
    return {
      id,
      name: (data.name as string) || '',
      description: data.description as string | undefined,
      fiscalYear: (data.fiscalYear as number) || new Date().getFullYear(),
      periodType: (data.periodType as 'monthly' | 'quarterly' | 'annual') || 'annual',
      startDate: (data.startDate as Timestamp)?.toDate() || new Date(),
      endDate: (data.endDate as Timestamp)?.toDate() || new Date(),
      scope: (data.scope as ExpenseBudget['scope']) || { type: 'organization' },
      totalBudget: (data.totalBudget as number) || 0,
      categoryBudgets: (data.categoryBudgets as ExpenseBudget['categoryBudgets']) || [],
      totalSpent: (data.totalSpent as number) || 0,
      totalCommitted: (data.totalCommitted as number) || 0,
      totalRemaining: (data.totalRemaining as number) || 0,
      percentUsed: (data.percentUsed as number) || 0,
      alertThresholds: (data.alertThresholds as ExpenseBudget['alertThresholds']) || {
        warningPercent: 75,
        criticalPercent: 90,
      },
      isActive: (data.isActive as boolean) ?? true,
      isFrozen: (data.isFrozen as boolean) ?? false,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      createdBy: data.createdBy as string | undefined,
    };
  }
}
